import * as HeroIconsOutline from '@heroicons/react/outline';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Error } from '../Error';
import { Label } from '../Label';
import { applyInputStyles } from '../style/util';
import { appendClassProps } from '../util';
// Component for conditionally forwarding ref
const UseOverrideRef = ({ forwardedRef, inputRef }) => {
    useImperativeHandle(forwardedRef, () => {
        return {
            ...inputRef?.current,
            focus: () => {
                inputRef?.current?.focus();
            },
            getBoundingClientRect: () => inputRef?.current.getBoundingClientRect(),
            offsetParent: inputRef?.current?.offsetParent
        };
    });
    return null;
};
/**
 - This is a controlled component for text input
 */
const TextArea = ({ autoFocus = false, revealErrorMessage = false, disabled = false, hideErrorSection = false, name, label, tooltip, placeholder, leftIcon, rightIcon, errorMessage, value, className, inputClassName, onChangeValue, onChange, onPressEnter, onBlur, onClickRightIcon }, ref) => {
    const inputRef = useRef(null);
    const [dirty, setDirty] = useState(false);
    let LeftIcon;
    let RightIcon;
    if (leftIcon)
        LeftIcon = HeroIconsOutline[leftIcon];
    if (rightIcon) {
        RightIcon = HeroIconsOutline[rightIcon];
    }
    const showErrorMessage = errorMessage && (revealErrorMessage || dirty) ? true : false;
    const handleChange = (event) => {
        const target = event.target;
        if (typeof onChange === 'function') {
            onChange(event);
        }
        else if (typeof onChangeValue === 'function') {
            onChangeValue(target.value);
        }
    };
    const handleBlur = () => {
        setDirty(true);
        if (onBlur)
            onBlur();
    };
    const handleClickRightIcon = () => {
        if (onClickRightIcon)
            onClickRightIcon();
    };
    const onClickLeftIcon = () => {
        inputRef?.current?.focus();
    };
    const onKeyUp = (event) => {
        if (event.key === 'Enter' && typeof onPressEnter === 'function')
            onPressEnter(event);
    };
    return (React.createElement("div", { "data-testid": "div", className: `flex flex-col text-blue-800${appendClassProps(className)}` },
        label && (React.createElement(Label, { dataTestId: "label", tooltip: tooltip }, label)),
        ref && Object.keys(ref).length > 0 ? (React.createElement(UseOverrideRef, { forwardedRef: ref, inputRef: inputRef })) : null,
        React.createElement("div", { className: "relative min-w-36 w-full" },
            leftIcon && (React.createElement("span", { className: `absolute inset-y-0 left-2 flex items-center text-gray-400  ${disabled ? 'cursor-not-allowed' : 'cursor-text'} `, onClick: onClickLeftIcon },
                React.createElement(LeftIcon, { "data-testid": "left-icon", className: "h-5 w-5 lg:h-6 lg:w-6" }))),
            rightIcon && (React.createElement("span", { className: `absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600`, onClick: handleClickRightIcon },
                React.createElement(RightIcon, { "data-testid": "right-icon", className: "h-5 w-5 lg:h-6 lg:w-6" }))),
            React.createElement("textarea", { ref: inputRef, name: name, onChange: handleChange, onBlur: handleBlur, onKeyUp: onKeyUp, "data-testid": "textArea", placeholder: placeholder, autoFocus: autoFocus, className: applyInputStyles({
                    leftIcon,
                    rightIcon,
                    showErrorMessage,
                    inputClassName,
                    disabled
                }), value: value, disabled: disabled })),
        !hideErrorSection && (React.createElement(Error, { errorMessage: errorMessage, showErrorMessage: showErrorMessage }))));
};
const WrappedTextArea = forwardRef(TextArea);
export * from './index.types';
export { WrappedTextArea as TextArea, TextArea as BareTextArea }; // for storybook (fails to properly parse component if wrapped with forwardRef)
//# sourceMappingURL=index.js.map